import { type ButtonProps } from '@heidi/design-system/button';
import { isMac } from '@heidi/utils';
import { type LucideIcon } from 'lucide-react';
import { memo } from 'react';
import { Key } from 'ts-key-enum';
import { Toolbar } from './toolbar-namespace';

const MemoButton = memo(Toolbar.Button);

export interface TextMenuButtonProps {
  icon: LucideIcon;
  /** Resolved tooltip text — translation stays host-side. */
  tooltip: string;
  /**
   * Single-letter shortcut rendered as ⌘/Ctrl + key in the tooltip (platform
   * resolved via `@heidi/utils`). Display-only — binding the shortcut is the
   * editor stack's job.
   */
  shortcutKey?: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  /**
   * Replaces `tooltip` (and hides the shortcut hint) while `disabled` — an
   * instructive line explaining why the action is unavailable, instead of
   * advertising a shortcut the button can't currently perform.
   */
  disabledTooltip?: string;
  iconSize?: number;
  className?: string;
  'data-testid'?: string;
  /** Toolbar button size; defaults to the Toolbar.Button default ('xs'). */
  size?: ButtonProps['size'];
}

/**
 * The generic text-menu button primitive (6.5/6.6 wave B-2): icon + tooltip +
 * shortcut display + active/click mechanics over the memoized toolbar button.
 * Everything product-shaped stays with the host wiring layer at the legacy
 * path — tooltip translation, the `button_clicked` analytics (and its
 * double-count opt-outs), action enums, and any post-click focus composition.
 */
export const TextMenuButton = ({
  icon: Icon,
  tooltip,
  shortcutKey,
  onClick,
  active,
  disabled,
  iconSize,
  disabledTooltip,
  className,
  'data-testid': testId,
  size,
}: TextMenuButtonProps) => {
  const showDisabledTooltip = Boolean(disabled && disabledTooltip);
  return (
    <MemoButton
      tooltip={showDisabledTooltip ? disabledTooltip : tooltip}
      tooltipShortcuts={
        shortcutKey && !showDisabledTooltip
          ? [
              { key: isMac ? Key.Meta : Key.Control, type: 'modifier' },
              { key: shortcutKey, type: 'letter' },
            ]
          : undefined
      }
      onClick={onClick}
      active={active}
      disabled={disabled}
      className={className}
      size={size}
      type="button"
      data-testid={testId}
    >
      <Icon size={iconSize} />
    </MemoButton>
  );
};
